/* Yumi & Cie — accents et recherche interne (v2, 01/08/2026)

   PROBLÈME MESURÉ
   La recherche plein texte de Shopify (/search) est sensible aux accents :
   « ecouteurs » remonte 130 produits, « écouteurs » seulement 70, et les 20
   qui manquent sont les casques QCY, ONIKUMA, BlitzWolf et Maono, dont seule
   l'étiquette « cat:ecouteurs » (sans accent) porte le mot. Dans l'autre sens,
   « eclairage » perd les rubans LED Gosund, Meross, NiteBird et Yeelight.

   CE QUI NE MARCHE PAS (mesuré le 31/07/2026, page /search et Storefront API)
   Aucune formulation ne reconstitue l'union des deux orthographes :
     « écouteurs ecouteurs »        115 résultats, 80 % de l'union, 12 hors sujet
     « écouteurs OR ecouteurs »     142 résultats, 56 % de l'union, 69 hors sujet
     « (écouteurs) OR (ecouteurs) » identique
     « "écouteurs" OR "ecouteurs" » identique
     « ecouteur* »                  148 résultats, 58 % de l'union
   Le moteur plein texte n'applique PAS la grammaire de recherche Shopify sur
   des mots libres. Seuls les termes qualifiés par un champ la respectent.

   CE QUI MARCHE
   Une seule requête, dans l'orthographe qui — mesure à l'appui — contient
   l'autre. On réécrit donc le mot au moment où la requête part, côté thème :
   aucune application, aucune saisie manuelle, aucune fiche produit modifiée.

   La recherche prédictive, elle, est DÉJÀ insensible aux accents (mesuré :
   « écouteurs » et « ecouteurs » renvoient les 10 mêmes suggestions) : on n'y
   touche pas. Seule la page de résultats est corrigée.

   AJOUT DU 01/08/2026 — les requêtes QUALIFIÉES passent intactes.
   Les filtres du catalogue construisent désormais des requêtes du type
   `tag:"cat:eclairage-connecte"`. Ce fichier réécrivait « eclairage » en
   « éclairage » À L'INTÉRIEUR du nom de l'étiquette : le nombre de résultats
   restait juste par chance — Shopify compare les étiquettes sans tenir compte
   des accents — mais l'état des cases devenait faux et l'adresse affichée était
   corrompue. La règle est déjà écrite plus haut : seuls les termes qualifiés
   respectent la grammaire, ils ne sont donc pas du texte libre et n'ont rien à
   faire dans cette table.
*/
(function () {
  'use strict';

  /* Table mesurée le 31/07/2026 sur les 4 977 produits du catalogue.
     Un couple n'y figure que si la mesure a prouvé les TROIS conditions :
       1. un gain en produits RÉELS (le mot est dans le titre, le type ou une
          étiquette — pas seulement au détour de la description) ;
       2. AUCUN produit réel perdu ;
       3. une première page de résultats non dégradée.

     Volontairement ABSENTS, mesure à l'appui :
       clé/cle, thermomètre, multimètre, télémètre, luxmètre, sonomètre
         -> la forme la moins fournie est la meilleure ; « cle usb » = 61
            résultats ciblés, « clé usb » = 832 fourre-tout ;
       écran, pièces, création, connecté, connectées, contrôleurs, vidéo,
       électroménager
         -> la réécriture ferait perdre des produits réels ;
       pièce
         -> +158 réels mais la première page tombe de 58 % à 38 % de pertinence ;
       télécommande, lumière, santé, sécurité, mètre, étanche, câble, mémoire,
       réglable, sécurisé, rétroéclairage, électrogène...
         -> les deux orthographes trouvent déjà les mêmes produits réels ;
            l'écart de TOTAL n'était que du bruit de description. */
  var CANON = {
    'écouteurs': 'ecouteurs',        /* +20 réels : casques QCY, ONIKUMA, BlitzWolf, Maono */
    'écouteur': 'ecouteur',          /* +2  */
    'eclairage': 'éclairage',        /* +11 réels : rubans LED Gosund, Meross, NiteBird, Yeelight */
    'controleur': 'contrôleur',      /* +58 réels : modules et interrupteurs AVATTO, Meross */
    'caméra': 'camera',              /* +7  */
    'caméras': 'cameras',            /* +2  */
    'velo': 'vélo',                  /* +3  */
    'detecteur': 'détecteur',        /* +2  */
    'detection': 'détection',        /* +2  */
    'cables': 'câbles',              /* +1  */
    'videoprojecteur': 'vidéoprojecteur', /* +1 */
    'ampèremétrique': 'amperemetrique',   /* +1 */
    'énergie': 'energie'             /* +1  */
  };

  /* Un mot = une suite de lettres ou de chiffres, accents compris. La
     réécriture est donc mot à mot : « thermomètre » n'est PAS « mètre », et
     « cle usb » traverse la table sans y toucher. */
  var WORD = /[0-9A-Za-zÀ-ÖØ-öø-ÿ]+/g;

  /* Une requête qualifiée n'est pas du texte libre : elle passe intacte. */
  var QUALIFIE = /\b(?:tag|vendor|product_type|id|sku|barcode|title|variants)\s*:/i;

  /* ------------------------------------------------------------------
     LE TIRET DE TÊTE EST UN OPÉRATEUR (ajout du 01/08/2026)

     Chez Shopify, « -32 » ne veut pas dire « trente-deux » mais « SANS 32 ».
     Un client qui colle le titre d'un produit vide donc ses propres résultats
     sans rien y comprendre. Mesuré ce jour sur le titre complet
     « UNI-T UT302C+ - Thermomètre infrarouge -32 à 1100 °C, laser annulaire » :

        tel quel                      0 produit
        tirets de tête neutralisés  192 produits, le bon au PREMIER rang
        référence seule              48 produits, le bon au premier rang

     On ne retire QUE le tiret qui commence un mot. Celui de « UNI-T » est à
     l'intérieur : il reste, et il compte. C'est la différence entre corriger
     une syntaxe accidentelle et rendre la recherche permissive.
     ------------------------------------------------------------------ */
  function sansExclusion(q) {
    return q
      .replace(/(^|\s)[-–—]+(?=[^\s-])/g, '$1') /* « -32 » -> « 32 »        */
      .replace(/(^|\s)[-–—]+(?=\s|$)/g, '$1') /* tiret isolé -> rien      */
      .replace(/\s{2,}/g, ' ') /* le tiret retiré laisse un trou */
      .trim();
  }

  /* ------------------------------------------------------------------
     CODE-BARRES À 14 CHIFFRES (ajout du 01/08/2026)

     Google Merchant Center et beaucoup d'étiquettes impriment le GTIN-14,
     c'est-à-dire l'EAN-13 précédé d'un zéro. Nos étiquettes produit, elles,
     portent l'EAN-13. Mesuré : « 6979033600201 » rend 1 produit, le bon ;
     « 06979033600201 » n'en rend aucun.

     On ne rabote QUE les codes plus longs que 13 chiffres. Un EAN-13 peut
     légitimement commencer par zéro — ceux dérivés de l'UPC américain, et le
     catalogue en compte — et le raboter casserait une recherche juste.
     ------------------------------------------------------------------ */
  function codeBarres(q) {
    var t = q.trim();
    if (!/^\d{13,14}$/.test(t)) return q;
    if (t.length <= 13) return q;
    var court = t.replace(/^0+/, '');
    return court.length >= 8 ? court : q;
  }

  function canon(q) {
    if (!q) return q;
    /* Une requête qualifiée — celle que construisent les filtres du catalogue,
       « tag:"cat:eclairage-connecte" » — n'est pas du texte libre : ni les
       accents, ni les tirets, ni les chiffres n'y ont le sens qu'ils ont dans
       une phrase. On n'y touche à aucun prix. */
    if (QUALIFIE.test(q)) return q;
    var t = codeBarres(sansExclusion(q));
    /* CODE-BARRES TAPÉ SEUL (ajout du 21/10/2026). Le moteur de Shopify ne
       retrouve pas tous les EAN tapés tels quels : sur 40 tirés au hasard,
       9 donnaient « aucun résultat » (ANYCUBIC 5905156103948, AOCHUAN
       6972467719679…) alors que la fiche porte bien ce code. Cherché DANS le
       code-barres et l'étiquette « EAN » posée par le robot, chacun sort, et
       lui seul. Seulement à 13 chiffres : pendant la frappe d'un code, la
       recherche reste celle du texte libre. Requête qualifiée = canon()
       n'y retouche plus (idempotent), et yc_q réaffiche la saisie. */
    if (/^\d{13}$/.test(t.trim())) {
      var ean = t.trim();
      return 'variants.barcode:' + ean + ' OR tag:' + ean;
    }
    return t.replace(WORD, function (w) {
      return CANON[w.toLowerCase()] || w;
    });
  }

  /* ------------------------------------------------------------------
     ZÉRO RÉSULTAT : ON RELÂCHE LA REQUÊTE (ajout du 01/08/2026, soir)

     Mesuré ce jour, sur le titre complet d'un produit du catalogue :
       « Merooss MOP320MA-EU - Prise double connectée d'extérieur IP44 »
           -> le produit sort
       « Meroooss MOP320MA-EU - Prise double connectée d'extérieur IP44 »
           -> AUCUN résultat
     Une lettre en trop passe, deux ne passent plus : le moteur de Shopify
     tolère une faute de frappe, pas davantage. Comme il exige que TOUS les
     mots correspondent, un seul mot mal tapé vide la page entière — alors que
     la référence « MOP320MA-EU », elle, désigne le produit sans ambiguïté.

     La parade ne consiste donc PAS à rendre la recherche permissive, ce qui
     noierait le visiteur. Elle consiste à ne relâcher QU'EN DERNIER RECOURS,
     quand la page est déjà vide, et à ne garder que ce qui identifie :

       1. les RÉFÉRENCES — un mot qui mêle lettres et chiffres. C'est ce que
          le visiteur copie d'une facture, d'une étiquette ou d'un titre ;
       2. à défaut, les deux mots les plus longs, qui portent le sens
          (« connectée », « extérieur ») là où « de », « pour », « et » n'en
          portent aucun.

     Le visiteur est prévenu de ce qui a été cherché à sa place : la barre
     réaffiche sa saisie d'origine, et `yc_relax=1` évite toute boucle si la
     requête relâchée ne donne rien non plus.
     ------------------------------------------------------------------ */
  var VIDES = /^(?:de|du|des|le|la|les|un|une|et|ou|pour|avec|sans|sur|en|au|aux|par|d|l)$/i;

  function relacher(q) {
    var mots = String(q || '')
      .replace(/[«»"'(),;:]/g, ' ')
      .split(/\s+/)
      .filter(function (m) { return m && !VIDES.test(m); });
    if (mots.length < 2) return null;

    var refs = mots.filter(function (m) {
      return m.length >= 4 && /\d/.test(m) && /[A-Za-z]/.test(m);
    });
    if (refs.length) return refs.join(' ');

    var tri = mots.slice().sort(function (a, b) { return b.length - a.length; });
    var deux = tri.slice(0, 2);
    /* Si les deux mots retenus sont deja toute la requete, relacher ne
       relache rien : inutile de recharger la page pour le meme resultat. */
    return deux.length === mots.length ? null : deux.join(' ');
  }

  function siAucunResultat() {
    if (!isSearchPath(window.location.pathname)) return;
    var params = new URLSearchParams(window.location.search);
    if (params.get('yc_relax')) return; /* deja relache : on s'arrete la */
    var form = document.getElementById('yc-filter-form');
    if (!form || form.getAttribute('data-total') !== '0') return;
    /* Une requete qualifiee vient des filtres, pas du clavier : zero resultat
       y est une reponse juste, pas une faute de frappe. */
    var brut = params.get('yc_q') || params.get('q') || '';
    if (!brut || QUALIFIE.test(brut)) return;
    var court = relacher(brut);
    if (!court) return;
    params.set('q', canon(court));
    params.set('yc_q', brut);
    params.set('yc_relax', '1');
    window.location.replace(window.location.pathname + '?' + params.toString());
  }

  /* /search, /fr/search, /search/ ... mais pas /search/suggest */
  function isSearchPath(p) {
    return /(^|\/)search$/.test(String(p).replace(/\/+$/, ''));
  }

  function sameOrigin(url) {
    return url.origin === window.location.origin;
  }

  /* 1. Départ du formulaire (barre de la bannière, barre de la page /search).
        On ne touche PAS au champ visible : on construit l'URL nous-mêmes et
        on garde la saisie d'origine dans yc_q pour la réafficher à l'arrivée. */
  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      try {
        if (!form || form.tagName !== 'FORM') return;
        if ((form.getAttribute('method') || 'get').toLowerCase() !== 'get') return;
        var url = new URL(form.getAttribute('action') || '', window.location.origin);
        if (!sameOrigin(url) || !isSearchPath(url.pathname)) return;
        var field = form.querySelector('[name="q"]');
        if (!field) return;
        var raw = String(field.value || '').trim();
        var fixed = canon(raw);
        if (!raw || fixed === raw) return;
        var params = new URLSearchParams(new FormData(form));
        params.set('q', fixed);
        params.set('yc_q', raw);
        e.preventDefault();
        window.location.assign(url.pathname + '?' + params.toString());
      } catch (err) {
        /* au moindre doute on laisse le formulaire partir tel quel */
      }
    },
    true
  );

  /* 2. Liens vers /search : « Voir tous les résultats pour … » du panneau de
        suggestions (injecté après coup, d'où la délégation), menus, etc. */
  document.addEventListener('click', function (e) {
    try {
      var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      var url = new URL(a.getAttribute('href'), window.location.href);
      if (!sameOrigin(url) || !isSearchPath(url.pathname)) return;
      var raw = url.searchParams.get('q');
      if (!raw) return;
      var fixed = canon(raw);
      if (fixed === raw) return;
      url.searchParams.set('q', fixed);
      url.searchParams.set('yc_q', raw);
      a.setAttribute('href', url.pathname + url.search);
    } catch (err) {}
  });

  /* 3. Arrivée sur /search : saisie directe dans la barre d'adresse,
        favori, autocomplétion du navigateur, lien externe — le formulaire
        n'est pas passé par là. On corrige et on réaffiche la saisie. */
  function onSearchPage() {
    if (!isSearchPath(window.location.pathname)) return;
    var params = new URLSearchParams(window.location.search);
    var raw = params.get('q') || '';
    var typed = params.get('yc_q');

    if (typed) {
      /* la requête est déjà canonique : on remet sous les yeux du client le
         mot qu'il a réellement tapé, accents compris */
      var inputs = document.querySelectorAll('form input[name="q"]');
      for (var i = 0; i < inputs.length; i++) inputs[i].value = typed;

      /* Requete RELACHEE : le visiteur doit savoir qu'on n'a pas cherche
         exactement ce qu'il a tape, sinon il croit que la boutique lui ment
         sur son propre texte. */
      if (params.get('yc_relax') && raw && raw !== typed) {
        var cible = document.querySelector('.yc-results, .yc-grid, main');
        if (cible && !document.querySelector('.yc-relax-note')) {
          var n = document.createElement('p');
          n.className = 'yc-relax-note';
          n.setAttribute('role', 'status');
          n.style.cssText = 'margin:0 0 14px;padding:10px 14px;border-radius:10px;'
            + 'background:rgba(232,150,46,.12);border:1px solid rgba(232,150,46,.35);'
            + 'font-size:.95rem;line-height:1.45;';
          n.textContent = 'Aucun résultat pour « ' + typed + ' ». Voici ce que donne « '
            + raw +' ».';
          cible.insertBefore(n, cible.firstChild);
        }
      }
      return;
    }
    var fixed = canon(raw);
    if (!raw || fixed === raw) return; /* canon() est idempotent : pas de boucle */
    params.set('q', fixed);
    params.set('yc_q', raw);
    window.location.replace(window.location.pathname + '?' + params.toString());
  }

  function auChargement() {
    onSearchPage();
    /* Le relachement vient APRES la canonisation : si la reecriture d'accent
       suffit a trouver des produits, on ne relache rien du tout. */
    siAucunResultat();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', auChargement);
  } else {
    auChargement();
  }

  /* exposé pour le banc de mesure (scripts/verify_accents_theme.py) */
  window.ycSearchCanonical = canon;
  window.ycSearchCanonTable = CANON;
})();
